/**
 * SideBar: logo row, grouped navigation, and a calm footer (credit line, Help + collapse, account). Release notes are
 * not in the sidebar: they show up once, as a card at the bottom left, when there is something new (see WhatsNew.tsx).
 *
 * Rhythm (matches the reference shell): logo 28px from the top with 32px before the nav; nav rows 40px with 14px labels (15px in the drawer) and 16px Lucide icons (18px in the drawer), grey,
 * 12px inside padding; the active row is a light blue pill (10px radius); icons lift slightly on hover; group labels sit 24px below the group
 * above. On short screens (height 860px or less) rows drop to 36px group gaps to 12px and the logo row tightens; if the list still does not
 * fit, only the nav scrolls, with a soft fade at the edge that has more, and the footer stays pinned.
 * Collapsing (lg and up) is remembered per browser and leaves icons with tooltips. In the phone drawer rows are 44px.
 * The admin side uses the same sidebar with `variant="admin"`: admin nav groups, and "Back to app" in place of the
 * credit line and Help.
 */
import { Icon } from "@iconify/react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";

import ProfileMenu from "./ProfileMenu";
import HelpMenu from "./HelpMenu";
import SideTooltip from "./SideTooltip";
import { ADMIN_NAV_GROUPS, NAV_GROUPS, isNavActive } from "./navConfig";
import IconButton from "@/components/ui/IconButton";
import { useUser } from "@/context/UserContext";
import { useFeatureFlags } from "@/context/FeatureFlagContext";
import { formatNumber } from "@/lib/format";
import posthog from "@/lib/posthog";

const COLLAPSE_KEY = "spidra:sidebar:collapsed";

interface SideBarProps {
  onClose?: () => void;
  /** Rendered as the mobile drawer (below lg). */
  isDrawer?: boolean;
  /** "admin": the admin nav and footer. */
  variant?: "app" | "admin";
}

const rowBase =
  "group flex items-center gap-3 rounded-[10px] font-medium transition-[background-color,color,transform] duration-150 cursor-pointer motion-safe:active:scale-[0.98]";
const rowIdle = "text-fg-secondary hover:bg-black/[0.04] hover:text-fg dark:hover:bg-white/[0.06]";
const rowActive = "bg-primary-subtle text-primary-text";
// Icons lift a touch on hover and settle back on press.
const iconMotion = "transition-transform duration-200 ease-out motion-safe:group-hover:-translate-y-px motion-safe:group-hover:scale-110 motion-safe:group-active:scale-95";

export default function SideBar({ onClose, isDrawer = false, variant = "app" }: SideBarProps) {
  const isAdmin = variant === "admin";
  const router = useRouter();
  const { user, loading: userLoading, setUser } = useUser();
  const { isEnabled } = useFeatureFlags();
  const creditsUsed = user?.creditUsage ?? 0;
  const creditLimit = user?.creditCap ?? null;
  const [collapsedPref, setCollapsedPref] = useState(false);

  useEffect(() => {
    try {
      setCollapsedPref(window.localStorage.getItem(COLLAPSE_KEY) === "1");
    } catch {
      /* storage blocked */
    }
  }, []);

  const toggleCollapsed = () => {
    setCollapsedPref(prev => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* storage blocked */
      }
      return next;
    });
  };

  const isCollapsed = collapsedPref && !isDrawer;
  const pct = creditLimit ? Math.min(100, (creditsUsed / creditLimit) * 100) : 0;
  // 40px rows (36px on short screens so 1024x768 keeps nearly everything in view); 44px in the phone drawer.
  const rowHeight = isDrawer ? "h-11" : "h-10 [@media(max-height:860px)]:h-9";
  // 16px icons on desktop so they sit quietly beside 14px labels; 18px in the phone drawer.
  const iconSize = isDrawer ? 18 : 16;
  const navRef = useRef<HTMLElement>(null);
  const [fade, setFade] = useState({ top: false, bottom: false });

  const checkFade = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    const top = el.scrollTop > 2;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight > 2;
    setFade(prev => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    checkFade();
    const ro = new ResizeObserver(checkFade);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [checkFade, isCollapsed]);

  const collapseButton = !isDrawer && (
    <SideTooltip label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"} enabled={isCollapsed}>
      <IconButton
        icon={<Icon icon={isCollapsed ? "lucide:panel-left-open" : "lucide:panel-left-close"} width={iconSize} height={iconSize} />}
        label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        tooltip={!isCollapsed}
        aria-expanded={!isCollapsed}
        size="md"
        className={isCollapsed ? "w-full" : "shrink-0"}
        onClick={toggleCollapsed}
      />
    </SideTooltip>
  );

  const groups = (isAdmin ? ADMIN_NAV_GROUPS : NAV_GROUPS).map(group => ({
    ...group,
    items: group.items.filter(item => !item.flagKey || isEnabled(item.flagKey)),
  })).filter(group => group.items.length > 0);

  return (
    <aside
      aria-label="Sidebar"
      className={`relative flex h-full flex-col border-r border-line bg-canvas transition-[width] duration-200 ease-out ${
        isCollapsed ? "w-16" : "w-64"
      }`}
    >
      {/* Logo row: the logo 28px from the top with 32px of room before the nav, so neither feels crowded
          (16px above and below on short screens). */}
      <div className={`flex shrink-0 items-center pb-8 pt-7 [@media(max-height:860px)]:pb-4 [@media(max-height:860px)]:pt-4 ${isCollapsed ? "justify-center px-2" : "justify-between pl-6 pr-3"}`}>
        <Link
          href={isAdmin ? "/admin" : "/dashboard"}
          aria-label={isAdmin ? "Spidra admin home" : "Spidra home"}
          className={`relative block rounded-md ${isCollapsed ? "h-9 w-9" : "h-9 w-[96px]"}`}
        >
          {isCollapsed ? (
            <Image src="/images/logo-icon.png" alt="" fill sizes="36px" className="object-contain" priority />
          ) : (
            <>
              <Image src="/images/logo.png" alt="" fill sizes="96px" className="object-contain object-left dark:hidden" priority />
              <Image
                src="/images/logo-white.png"
                alt=""
                fill
                sizes="96px"
                className="hidden object-contain object-left dark:block"
                priority
              />
            </>
          )}
        </Link>
        {isDrawer && <IconButton icon="lucide:x" label="Close navigation" size="lg" onClick={onClose} data-drawer-close="" />}
      </div>

      {/* Nav: the only part that scrolls (short screens only), with a soft fade at whichever edge has more. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <nav
          ref={navRef}
          onScroll={checkFade}
          aria-label={isAdmin ? "Admin" : "Main"}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-1 pt-1 [scrollbar-width:thin] [@media(max-height:860px)]:pb-0.5"
        >
          <div>
            {groups.map((group, groupIdx) => (
              <div
                key={group.heading || `group-${groupIdx}`}
                className={
                  groupIdx === 0
                    ? ""
                    : isCollapsed
                      ? "mt-3 border-t border-line pt-3"
                      : "mt-6 [@media(max-height:860px)]:mt-3"
                }
              >
                {group.heading && !isCollapsed && (
                  <p className="mb-1.5 px-3 text-xs font-medium leading-4 text-fg-muted">{group.heading}</p>
                )}
                <ul>
                  {group.items.map(item => {
                    const isActive = !item.external && isNavActive(item.link, router.pathname);
                    const RowLink = item.external ? "a" : Link;
                    return (
                      <li key={item.label}>
                        <SideTooltip label={item.badge ? `${item.label} (${item.badge.toLowerCase()})` : item.label} enabled={isCollapsed}>
                          <RowLink
                            href={item.link}
                            {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            aria-current={isActive ? "page" : undefined}
                            aria-label={
                              item.external
                                ? `${item.label} (opens in a new tab)`
                                : isCollapsed
                                  ? item.badge
                                    ? `${item.label}, ${item.badge.toLowerCase()}`
                                    : item.label
                                  : undefined
                            }
                            onClick={onClose}
                            className={`${rowBase} ${rowHeight} ${isDrawer ? "text-15" : "text-sm"} ${isCollapsed ? "relative justify-center px-0" : "px-3"} ${
                              isActive ? rowActive : rowIdle
                            }`}
                          >
                            <Icon
                              icon={item.icon}
                              width={iconSize}
                              height={iconSize}
                              className={`shrink-0 ${iconMotion} ${isActive ? "text-primary-text" : "text-fg-secondary group-hover:text-fg"}`}
                              aria-hidden="true"
                            />
                            {isCollapsed && item.badge && (
                              <span className="absolute right-2 top-1.5 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                            )}
                            {!isCollapsed && (
                              <>
                                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                                {item.badge && (
                                  <span className={`shrink-0 rounded-full px-2 py-px text-xs font-medium leading-4 text-primary-text ${isActive ? "bg-surface" : "bg-primary-subtle"}`}>
                                    <span className="sr-only">, </span>
                                    {item.badge}
                                  </span>
                                )}
                                {item.external && <Icon icon="lucide:arrow-up-right" width={14} height={14} className="shrink-0 text-fg-muted" aria-hidden="true" />}
                              </>
                            )}
                          </RowLink>
                        </SideTooltip>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-canvas to-transparent transition-opacity duration-150 ${
            fade.top ? "opacity-100" : "opacity-0"
          }`}
        />
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-canvas to-transparent transition-opacity duration-150 ${
            fade.bottom ? "opacity-100" : "opacity-0"
          }`}
        />
      </div>

      {/* Footer: credit line, Help + collapse, then the account row under a hairline. */}
      <div className="shrink-0 border-t border-line px-3 pb-2 pt-2.5">
        {isAdmin ? (
          <div className={isCollapsed ? "flex flex-col gap-1" : "flex items-center gap-1"}>
            <SideTooltip label="Back to app" enabled={isCollapsed}>
              <Link
                href="/dashboard"
                onClick={onClose}
                aria-label={isCollapsed ? "Back to app" : undefined}
                className={`${rowBase} h-9 text-sm ${isCollapsed ? "justify-center px-0" : "min-w-0 flex-1 px-3"} ${rowIdle}`}
              >
                <Icon icon="lucide:arrow-left" width={iconSize} height={iconSize} className={`shrink-0 ${iconMotion} text-fg-secondary group-hover:text-fg`} aria-hidden="true" />
                {!isCollapsed && <span className="min-w-0 flex-1 truncate">Back to app</span>}
              </Link>
            </SideTooltip>
            {collapseButton}
          </div>
        ) : isCollapsed ? (
          <div className="flex flex-col gap-1">
            <HelpMenu collapsed rowHeight="h-9" iconSize={iconSize} />
            {collapseButton}
          </div>
        ) : (
          <>
            <div className="px-3">
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <Link
                  href="/dashboard/usage"
                  onClick={onClose}
                  className="min-w-0 truncate rounded-sm text-fg-muted hover:text-fg"
                  aria-label={
                    creditLimit
                      ? `${formatNumber(creditsUsed)} of ${formatNumber(creditLimit)} credits used${user?.plan ? ` on the ${user.plan} plan` : ""}. View usage`
                      : "View usage"
                  }
                >
                  {userLoading ? (
                    "Credits"
                  ) : (
                    <>
                      <span className="tabular-nums font-medium text-fg-secondary">{formatNumber(creditsUsed)}</span>
                      <span className="tabular-nums"> of {creditLimit ? formatNumber(creditLimit) : "-"} credits</span>
                    </>
                  )}
                </Link>
                <Link
                  href="/dashboard/billing"
                  onClick={() => {
                    posthog.capture("upgrade_cta_clicked", { location: "sidebar" });
                    onClose?.();
                  }}
                  className="shrink-0 rounded-sm font-medium text-primary-text hover:underline"
                >
                  Upgrade
                </Link>
              </div>
              <div
                className="mt-2 h-1 w-full overflow-hidden rounded-full bg-black/[0.07] dark:bg-white/10"
                role="progressbar"
                aria-label="Credits used"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(pct)}
              >
                <div
                  className={`h-full rounded-full transition-[width] duration-300 ${pct >= 90 ? "bg-danger" : "bg-primary"} ${
                    userLoading ? "animate-pulse" : ""
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>

            <div className="mt-2 flex items-center gap-1">
              <div className="min-w-0 flex-1">
                <HelpMenu rowHeight="h-9" iconSize={iconSize} />
              </div>
              {collapseButton}
            </div>
          </>
        )}

        <div className="mt-1.5 border-t border-line pt-1.5">
          <ProfileMenu user={user} setUser={setUser} isCollapsed={isCollapsed} onClose={onClose} />
        </div>
      </div>
    </aside>
  );
}
